import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { getMatchWithDetails, getTeams, calculateDuration } from '@/lib/supabase';
import { Team, SetRecord, Rally } from '@/types';
import AppHeader from '@/components/AppHeader';
import { TeamLogo } from '@/components/TeamLogo';
import RallyHistoryModal from '@/components/modals/RallyHistoryModal';
import { getSetScoreColor } from '@/lib/setScoreColor';

interface MatchViewData {
  id: string;
  match_number: number;
  date: string;
  match_time: string | null;
  match_type: 'league' | 'playoff';
  status: string;
  team_a_id: string;
  team_b_id: string;
  winner_id: string | null;
  started_at: string | null;
  ended_at: string | null;
  toss_won_by_id: string | null;
  toss_choice: string | null;
  best_catcher_ids: string[] | null;
  tournament_id: string | null;
  tournament_name?: string;
  sets: (SetRecord & { rallies?: Rally[] })[];
  winning_point: number | null;
  golden_point: number | null;
}

export default function MatchView({ onSignOut }: { onSignOut: () => void }) {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  useAuth();
  const [match, setMatch] = useState<MatchViewData | null>(null);
  const [teams, setTeams] = useState<Map<string, Team>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyModal, setHistoryModal] = useState<{
    setNumber: number;
    setRecord: SetRecord;
    rallies: Rally[];
  } | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async (silent = false) => {
    if (!matchId) return;
    if (!silent) setLoading(true);
    if (silent) setRefreshing(true);
    setError(null);
    try {
      const [matchData, teamsData] = await Promise.all([
        getMatchWithDetails(matchId),
        getTeams(),
      ]);
      setMatch(matchData as MatchViewData);
      const teamMap = new Map<string, Team>();
      teamsData.forEach((t: any) => teamMap.set(t.id, {
        id: t.id,
        name: t.name,
        shortName: t.short_name,
        color: t.color,
        logoUrl: t.logo_url,
        players: (t.players || []).map((p: any) => ({
          id: p.id,
          name: p.name,
          number: p.number,
          teamId: p.team_id,
        })),
      }));
      setTeams(teamMap);
      setLastUpdated(new Date());
    } catch (err) {
      if (!silent) setError('Failed to load match data');
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
      if (silent) setRefreshing(false);
    }
  }, [matchId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 5 seconds for live matches only
  useEffect(() => {
    if (!match) return;
    const isLive = match.status === 'live' || match.status === 'set-won' || match.status === 'first-server';
    if (!isLive) return;
    const interval = setInterval(() => loadData(true), 5000);
    return () => clearInterval(interval);
  }, [match?.status, loadData]);

  // Update "Updated X ago" indicator every second
  const [, setNowTick] = useState(0);
  useEffect(() => {
    if (!lastUpdated) return;
    const t = setInterval(() => setNowTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, [lastUpdated]);

  const handleManualRefresh = () => loadData(true);

  const updatedSecondsAgo = lastUpdated
    ? Math.floor((Date.now() - lastUpdated.getTime()) / 1000)
    : null;

  const updatedLabel = updatedSecondsAgo === null
    ? null
    : updatedSecondsAgo < 60
      ? `Updated ${updatedSecondsAgo}s ago`
      : `Updated ${lastUpdated!.toLocaleTimeString()}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !match) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-error text-lg mb-4">{error || 'Match not found'}</p>
          <button
            onClick={() => navigate('/scorer')}
            className="btn-primary bg-primary text-white"
          >
            BACK TO DASHBOARD
          </button>
        </div>
      </div>
    );
  }

  const teamA = teams.get(match.team_a_id);
  const teamB = teams.get(match.team_b_id);
  const winner = match.winner_id ? teams.get(match.winner_id) : null;
  const duration = calculateDuration(match.started_at, match.ended_at);
  const isLive = match.status === 'live' || match.status === 'set-won' || match.status === 'first-server';
  const isCompleted = match.status === 'match-won';

  // Determine current set and scores — sets come back as raw DB rows (snake_case)
  const currentSet = match.sets?.find((s: any) => !(s.endedAt ?? s.ended_at));
  const completedSets = match.sets?.filter((s: any) => (s.endedAt ?? s.ended_at)) || [];
  const setsWonA = completedSets.filter((s: any) => (s.winnerId ?? s.winner_id) === match.team_a_id).length;
  const setsWonB = completedSets.filter((s: any) => (s.winnerId ?? s.winner_id) === match.team_b_id).length;

  const totalRallies = (match.sets || []).reduce((sum, s) => sum + (s.rallies?.length || 0), 0);
  const bestCatchers = (match.best_catcher_ids || []).map(id => {
    const player = teamA?.players.find(p => p.id === id) || teamB?.players.find(p => p.id === id);
    return player;
  }).filter(Boolean);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        onSignOut={onSignOut}
        title={`Match #${match.match_number}`}
        subtitle={match.tournament_name || `${match.date} · ${match.match_type.toUpperCase()}`}
        navIcon="back"
        onNavigate={() => {
          if (match.tournament_id) {
            navigate(`/tournament/${match.tournament_id}`);
          } else {
            navigate('/scorer');
          }
        }}
      />

      <div className="flex-1 overflow-y-auto px-4 py-4 safe-area-bottom">
        {/* Role Badge */}
        <div className="mb-3 flex items-center justify-between gap-2">
          <span className="inline-block px-3 py-1 text-xs font-bold rounded-full bg-info/15 text-info">
            VIEWER MODE
          </span>
          <div className="flex items-center gap-2">
            {updatedLabel && (
              <span className="text-xs text-text-muted">{updatedLabel}</span>
            )}
            <button
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full bg-surface-raised border border-border text-text-primary hover:border-text-muted transition-colors disabled:opacity-50"
            >
              <span className={refreshing ? 'inline-block animate-spin' : ''}>
                {refreshing ? '\u23F3' : '\u{1F504}'}
              </span>
              {refreshing ? 'UPDATING' : 'REFRESH'}
            </button>
          </div>
        </div>

        {/* Status */}
        <div className="mb-4">
          {isLive && (
            <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-bold rounded-full bg-warning/20 text-warning">
              <span className="w-2 h-2 rounded-full bg-warning animate-pulse" />
              LIVE
            </span>
          )}
          {isCompleted && (
            <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-bold rounded-full bg-success/20 text-success">
              COMPLETED
            </span>
          )}
          {match.status === 'first-server' && (
            <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-bold rounded-full bg-warning/20 text-warning">
              WAITING TO START
            </span>
          )}
          {match.status === 'scheduled' && (
            <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-bold rounded-full bg-info/20 text-info">
              SCHEDULED
            </span>
          )}
        </div>

        {/* Teams */}
        <div className="card mb-4">
          <div className="flex items-center justify-between">
            <div className="text-center flex-1">
              {teamA && (
                <>
                  <div className="mx-auto mb-2">
                    <TeamLogo team={teamA} size="lg" />
                  </div>
                  <p className="font-bold text-lg" style={{ color: teamA.color }}>{teamA.name}</p>
                  <p className="text-xs text-text-muted">{teamA.shortName}</p>
                </>
              )}
            </div>
            <div className="px-4">
              <span className="text-2xl font-bold text-text-muted">VS</span>
            </div>
            <div className="text-center flex-1">
              {teamB && (
                <>
                  <div className="mx-auto mb-2">
                    <TeamLogo team={teamB} size="lg" />
                  </div>
                  <p className="font-bold text-lg" style={{ color: teamB.color }}>{teamB.name}</p>
                  <p className="text-xs text-text-muted">{teamB.shortName}</p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Scoreboard */}
        {(isLive || isCompleted) && (
          <div className="card mb-4">
            <div className="grid grid-cols-2 divide-x divide-border">
              <div className="text-center py-4">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <TeamLogo team={teamA as any} size="sm" />
                  <span className="text-xs font-semibold uppercase" style={{ color: teamA?.color }}>
                    {teamA?.shortName}
                  </span>
                </div>
                <p className="font-mono-nums text-5xl font-bold text-text-primary">
                  {String((currentSet as any)?.scoreA ?? (currentSet as any)?.score_a ?? 0).padStart(2, '0')}
                </p>
                <div className="flex justify-center gap-1 mt-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span
                      key={i}
                      className={`w-2.5 h-2.5 rounded-full ${
                        i < setsWonA ? 'bg-team-a' : 'bg-border'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <div className="text-center py-4">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <TeamLogo team={teamB as any} size="sm" />
                  <span className="text-xs font-semibold uppercase" style={{ color: teamB?.color }}>
                    {teamB?.shortName}
                  </span>
                </div>
                <p className="font-mono-nums text-5xl font-bold text-text-primary">
                  {String((currentSet as any)?.scoreB ?? (currentSet as any)?.score_b ?? 0).padStart(2, '0')}
                </p>
                <div className="flex justify-center gap-1 mt-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span
                      key={i}
                      className={`w-2.5 h-2.5 rounded-full ${
                        i < setsWonB ? 'bg-team-b' : 'bg-border'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
            {currentSet && (
              <div className="text-center py-2 border-t border-border">
                <p className="text-xs text-text-muted">
                  SET {(currentSet as any).setNumber ?? (currentSet as any).set_number} · IN PROGRESS
                </p>
              </div>
            )}
          </div>
        )}

        {/* Set Scores */}
        {match.sets && match.sets.length > 0 && (
          <div className="card mb-4">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Set Scores</h3>
            <div className="space-y-2">
              {match.sets.map((set) => {
                const s = set as any;
                const winnerId = s.winnerId ?? s.winner_id;
                const setWinner = winnerId ? teams.get(winnerId) : null;
                const { color: scoreColor } = getSetScoreColor(set, teamA, teamB);
                return (
                  <div key={set.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <span className="text-xs font-semibold text-text-muted w-16">Set {s.setNumber ?? s.set_number}</span>
                    <div className="flex items-center gap-3 flex-1 justify-center">
                      <span className="font-mono text-lg font-bold" style={{ color: scoreColor ?? undefined }}>
                        {s.scoreA ?? s.score_a}
                      </span>
                      <span className="text-text-muted">-</span>
                      <span className="font-mono text-lg font-bold" style={{ color: scoreColor ?? undefined }}>
                        {s.scoreB ?? s.score_b}
                      </span>
                    </div>
                    <div className="w-24 text-right">
                      {setWinner && (
                        <span className="text-xs font-bold" style={{ color: setWinner.color }}>
                          {setWinner.shortName}
                        </span>
                      )}
                      {(s.endedAt ?? s.ended_at) && !setWinner && (
                        <span className="text-xs text-text-muted">In Progress</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Match Info */}
        <div className="card mb-4">
          <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Match Info</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-text-muted">Date</span>
              <span className="text-text-primary">{new Date(match.date).toLocaleDateString()}</span>
            </div>
            {match.match_time && (
              <div className="flex justify-between">
                <span className="text-text-muted">Time</span>
                <span className="text-text-primary">{match.match_time}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-text-muted">Type</span>
              <span className="text-text-primary capitalize">{match.match_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-muted">Status</span>
              <span className="text-text-primary capitalize">{match.status.replace('-', ' ')}</span>
            </div>
            {duration && (
              <div className="flex justify-between">
                <span className="text-text-muted">Duration</span>
                <span className="text-text-primary">{duration}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-text-muted">Total Rallies</span>
              <span className="text-text-primary">{totalRallies}</span>
            </div>
            {match.winning_point && (
              <div className="flex justify-between">
                <span className="text-text-muted">Winning Point</span>
                <span className="text-text-primary">{match.winning_point}</span>
              </div>
            )}
            {match.golden_point && (
              <div className="flex justify-between">
                <span className="text-text-muted">Golden Point</span>
                <span className="text-text-primary">{match.golden_point}</span>
              </div>
            )}
          </div>
        </div>

        {/* Winner */}
        {winner && (
          <div className="card bg-success/10 border-success/30 mb-4">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Winner</h3>
            <div className="flex items-center gap-2">
              <TeamLogo team={winner} size="sm" />
              <span className="font-bold text-success">{winner.name}</span>
            </div>
          </div>
        )}

        {/* Best Catchers */}
        {bestCatchers.length > 0 && (
          <div className="card mb-4">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Best Catcher(s)</h3>
            <div className="flex flex-wrap gap-2">
              {bestCatchers.map((player) => (
                <span key={player!.id} className="px-3 py-1 bg-primary/10 text-primary text-xs font-bold rounded-full">
                  {player!.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Rally History */}
        {(isLive || isCompleted) && match.sets && match.sets.length > 0 && (
          <div className="card mb-4">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Rally History</h3>
            <div className="space-y-2">
              {match.sets.map((set) => (
                <button
                  key={set.id}
                  onClick={() => setHistoryModal({
                    setNumber: set.setNumber,
                    setRecord: set,
                    rallies: set.rallies || [],
                  })}
                  className="w-full flex items-center justify-between py-2 px-3 rounded-lg bg-surface-raised hover:bg-border transition-colors text-left"
                >
                  <span className="text-sm font-medium text-text-primary">Set {set.setNumber}</span>
                  <span className="text-xs text-text-muted">{set.rallies?.length || 0} rallies</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Toss Info */}
        {match.toss_won_by_id && (
          <div className="card mb-4">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Toss</h3>
            <div className="flex items-center gap-2">
              {(() => {
                const tossWinner = teams.get(match.toss_won_by_id!);
                return tossWinner ? (
                  <>
                    <TeamLogo team={tossWinner} size="sm" />
                    <span className="text-sm text-text-primary">
                      {tossWinner.name} won toss and chose {match.toss_choice?.toLowerCase()}
                    </span>
                  </>
                ) : null;
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Rally History Modal */}
      {historyModal && teamA && teamB && (
        <RallyHistoryModal
          setNumber={historyModal.setNumber}
          setRecord={historyModal.setRecord}
          rallies={historyModal.rallies}
          teamA={teamA}
          teamB={teamB}
          onClose={() => setHistoryModal(null)}
        />
      )}
    </div>
  );
}
